import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import DOMPurify from 'dompurify';

const TINY_MCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

export interface RegulamentoEditModalProps {
  open: boolean;
  initialValue: string;
  onSave: (html: string) => void;
  onClose: () => void;
}

const RegulamentoEditModal: React.FC<RegulamentoEditModalProps> = ({
  open,
  initialValue,
  onSave,
  onClose,
}) => {
  const [value, setValue] = useState(initialValue);
  const editorRef = useRef<any>(null);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
    }
  }, [open, initialValue]);

  const handleSave = useCallback(() => {
    const clean = DOMPurify.sanitize(value);
    onSave(clean);
    onClose();
  }, [value, onSave, onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && e.ctrlKey) handleSave();
    };
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.classList.add('modal-open');
      setTimeout(() => editorRef.current?.focus(), 0);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [open, onClose, handleSave]);

  if (!open) return null;

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        onMouseDown={handleBackdrop}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title w-100 text-center">Editar regulamento</h5>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <Editor
                apiKey={TINY_MCE_API_KEY || ''}
                tinymceScriptSrc={`https://cdn.tiny.cloud/1/${TINY_MCE_API_KEY || ''}/tinymce/6/tinymce.min.js`}
                onInit={(_evt, editor) => {
                  editorRef.current = editor;
                  setTimeout(() => editor.focus(), 0);
                }}
                value={value}
                onEditorChange={(content) => setValue(content)}
                init={{
                  height: 300,
                  menubar: false,
                  plugins: ['link', 'lists'],
                  toolbar:
                    'undo redo | bold italic underline | bullist numlist | blockquote | link | removeformat',
                  placeholder: 'Escreva o regulamento do torneio…',
                }}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link" onClick={onClose} aria-label="Cancelar">
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSave} aria-label="Salvar">
                Salvar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegulamentoEditModal;
